/**
 * PricingPage - Displays available plans with dynamic data from API.
 * 
 * @security Uses public API endpoint (no auth required for viewing plans)
 * @performance Plans cached for 5 minutes, lazy checkout
 * @scalability Uses centralized billing hooks
 */
import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { usePlans, useSubscription, useCheckout, formatPrice } from '../hooks/useBilling'
import type { Plan, BillingInterval } from '../types/billing'
import { ChevronDown, Send, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../lib/utils'
import { api } from '../lib/api'
import { getFeatureLabel, formatLimitsAsFeatures } from '../lib/pricing-constants'

// FAQ data
const FAQ_ITEMS = [
  {
    q: "Puis-je changer de forfait à tout moment ?",
    a: "Oui, vous pouvez upgrader ou downgrader votre forfait à tout moment. La différence sera calculée au prorata."
  },
  {
    q: "Comment fonctionne la période d'essai ?",
    a: "Vous bénéficiez de 14 jours d'essai gratuit sur tous les forfaits payants. Aucune carte bancaire requise pour commencer."
  },
  {
    q: "Que se passe-t-il si je dépasse mes quotas ?",
    a: "Vous recevrez des alertes à 80% et 100% de votre quota. Au-delà, les conversations seront mises en pause jusqu'au prochain cycle de facturation ou jusqu'à un upgrade."
  },
  {
    q: "Quelle est la différence entre les forfaits ?",
    a: "Le volume de messages, le nombre d'agents et les fonctionnalités avancées (API, intégrations) sont les principales différences."
  },
  {
    q: "Proposez-vous des offres personnalisées ?",
    a: "Oui, utilisez le formulaire de contact ci-dessous pour nous décrire vos besoins spécifiques et obtenir une offre sur-mesure."
  }
]

// CTA button styles
const ctaClass = {
  primary:
    'mt-6 inline-flex justify-center rounded-full bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200 transition-colors duration-200 disabled:opacity-50',
  secondary:
    'mt-6 inline-flex justify-center rounded-full border border-zinc-300 dark:border-zinc-700 px-5 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-100 hover:border-zinc-400 dark:hover:border-zinc-500 transition-colors duration-200 disabled:opacity-50',
  highlight:
    'mt-6 inline-flex justify-center rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 text-white px-6 py-2.5 text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:opacity-90 transition-all duration-200 disabled:opacity-50',
}

export default function PricingPage() {
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('month')
  const [faqOpenIndex, setFaqOpenIndex] = useState<number | null>(null)
  
  // Contact form state
  const [contactMessage, setContactMessage] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactSending, setContactSending] = useState(false)
  const [contactSent, setContactSent] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  
  // Get user info from localStorage
  const [user, setUser] = useState<{ email?: string; first_name?: string; last_name?: string } | null>(null)
  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('user') || 'null')
      setUser(parsed)
      // Pre-fill name if available
      const fullName = [parsed?.first_name, parsed?.last_name].filter(Boolean).join(' ')
      if (fullName) setContactName(fullName)
    } catch {
      setUser(null)
    }
  }, [])
  
  // Fetch plans from API
  const { plans, loading: plansLoading, error: plansError } = usePlans()
  const { subscription, hasSubscription } = useSubscription()
  const { createCheckout, loading: checkoutLoading } = useCheckout()
  
  // Current plan slug
  const currentPlanSlug = subscription?.plan_slug || null
  
  // Calculate max savings percentage across all plans dynamically
  const maxSavingsPercent = useMemo(() => {
    return plans.reduce((max, plan) => {
      if (plan.price_monthly <= 0 || !plan.price_yearly) return max;
      const monthlyTotal = plan.price_monthly * 12;
      const savings = ((monthlyTotal - plan.price_yearly) / monthlyTotal) * 100;
      return Math.max(max, Math.round(savings));
    }, 0);
  }, [plans]);

  // Handle plan selection
  const handleSelectPlan = useCallback(async (plan: Plan) => {
    if (plan.price_monthly === 0) {
      // Free plan - no checkout needed
      return
    }
    
    if (plan.slug === currentPlanSlug) {
      // Already on this plan
      return
    }
    
    // Create checkout session
    await createCheckout(plan.public_id, billingInterval)
  }, [createCheckout, billingInterval, currentPlanSlug])
  
  // Compute display price for a plan
  const getDisplayPrice = useCallback((plan: Plan) => {
    if (billingInterval === 'year' && plan.price_yearly) {
      return {
        price: plan.price_yearly / 12,
        total: plan.price_yearly,
        savings: Math.round(((plan.price_monthly * 12) - plan.price_yearly) / (plan.price_monthly * 12) * 100)
      }
    }
    return {
      price: plan.price_monthly,
      total: plan.price_monthly,
      savings: 0
    }
  }, [billingInterval])
  
  // Get CTA style based on plan
  const getCtaStyle = (plan: Plan): keyof typeof ctaClass => {
    if (plan.is_featured) return 'highlight'
    if (plan.price_monthly === 0) return 'secondary'
    return 'primary'
  }

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background gradient - Standard layout */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      {/* Main content - Full width */}
      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        <header className="mb-10">
          <p className="text-base text-zinc-600 dark:text-zinc-300 max-w-3xl">
            Nos offres s&rsquo;adaptent à la maturité de votre projet IA. Hébergement UE, conformité RGPD et performance garantie.
          </p>
          
          {/* Billing toggle */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setBillingInterval('month')}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                billingInterval === 'month'
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/40'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
              }`}
            >
              Mensuel
            </button>
            <div
              role="switch"
              aria-checked={billingInterval === 'year'}
              tabIndex={0}
              onClick={() => setBillingInterval(prev => prev === 'month' ? 'year' : 'month')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setBillingInterval(prev => prev === 'month' ? 'year' : 'month')
                }
              }}
              className={`relative h-8 w-14 cursor-pointer rounded-full transition-colors ${
                billingInterval === 'year' ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-700'
              }`}
            >
              <span className="sr-only">Basculer la facturation</span>
              <span
                className={`absolute top-1 left-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                  billingInterval === 'year' ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </div>
            <button
              type="button"
              onClick={() => setBillingInterval('year')}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                billingInterval === 'year'
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/40'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
              }`}
            >
              Annuel{maxSavingsPercent > 0 ? ` (-${maxSavingsPercent}%)` : ''}
            </button>
          </div>
          {billingInterval === 'year' && maxSavingsPercent > 0 && (
            <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-300">
              Économisez ~{maxSavingsPercent}% avec la facturation annuelle
            </p>
          )}
        </header>

        {/* Loading state */}
        {plansLoading && (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse rounded-3xl border border-zinc-200/80 bg-white/95 p-6 dark:border-zinc-700/60 dark:bg-zinc-900/80">
                <div className="h-6 w-24 rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="mt-4 h-4 w-full rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="mt-6 h-10 w-32 rounded bg-zinc-200 dark:bg-zinc-700" />
                <div className="mt-4 space-y-2">
                  {[1, 2, 3].map(j => (
                    <div key={j} className="h-4 w-full rounded bg-zinc-200 dark:bg-zinc-700" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {plansError && (
          <div className="text-center py-12">
            <p className="text-red-500">Erreur lors du chargement des plans</p>
          </div>
        )}

        {/* Plans grid */}
        {!plansLoading && !plansError && (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {plans.map((plan) => {
              const { price, total, savings } = getDisplayPrice(plan)
              const isCurrentPlan = plan.slug === currentPlanSlug
              const isFree = plan.price_monthly === 0

              return (
                <article
                  key={plan.slug}
                  className={`group relative flex flex-col rounded-3xl border border-zinc-200/80 bg-white/95 p-6 shadow-sm backdrop-blur-sm transition-all duration-300 dark:border-zinc-700/60 dark:bg-zinc-900/80 hover:-translate-y-1 hover:shadow-xl dark:hover:shadow-zinc-900/60 ${
                    plan.is_featured ? 'ring-2 ring-emerald-400/50 dark:ring-emerald-500/40' : ''
                  } ${isCurrentPlan ? 'ring-2 ring-blue-400/50' : ''}`}
                >
                  {/* Featured badge */}
                  {plan.is_featured && !isCurrentPlan && (
                    <span className="absolute -top-3 right-5 inline-flex h-7 items-center rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 px-3 text-xs font-semibold uppercase tracking-wide text-white shadow-lg">
                      Populaire
                    </span>
                  )}
                  
                  {/* Current plan badge */}
                  {isCurrentPlan && (
                    <span className="absolute -top-3 right-5 inline-flex h-7 items-center rounded-full bg-blue-500 px-3 text-xs font-semibold uppercase tracking-wide text-white shadow-lg">
                      Plan actuel
                    </span>
                  )}
                  
                  <div className="min-h-[1.75rem]" />
                  
                  <h3 className="text-xl font-semibold text-zinc-900 dark:text-white">{plan.name}</h3>
                  <p className="mt-2 min-h-[72px] text-sm text-zinc-500 dark:text-zinc-400">
                    {plan.description || `Plan ${plan.name}`}
                  </p>
                  
                  {/* Price */}
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-zinc-900 dark:text-white">
                      {isFree ? 'Gratuit' : formatPrice(price, plan.currency)}
                    </span>
                    {!isFree && (
                      <span className="text-sm text-zinc-500 dark:text-zinc-400">/mois</span>
                    )}
                  </div>
                  
                  {/* Yearly info */}
                  <div className="mt-2 min-h-[2.5rem]">
                    {billingInterval === 'year' && !isFree && plan.price_yearly && savings > 0 && (
                      <p className="text-xs font-medium text-emerald-600 dark:text-emerald-300">
                        {formatPrice(total, plan.currency)} facturé/an — économie de {savings}%
                      </p>
                    )}
                  </div>
                  
                  {/* Limits */}
                  <div className="mt-4 space-y-1 text-sm text-zinc-600 dark:text-zinc-300">
                    <div className="flex justify-between">
                      <span>Agents</span>
                      <span className="font-medium">
                        {plan.limits.max_agents === -1 ? 'Illimité' : plan.limits.max_agents}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Conversations/mois</span>
                      <span className="font-medium">
                        {plan.limits.max_conversations_per_month === -1 
                          ? 'Illimité' 
                          : plan.limits.max_conversations_per_month.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Documents</span>
                      <span className="font-medium">
                        {plan.limits.max_knowledge_items === -1 
                          ? 'Illimité' 
                          : plan.limits.max_knowledge_items.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    {plan.limits.max_storage_mb != null && (
                      <div className="flex justify-between">
                        <span>Stockage</span>
                        <span className="font-medium">
                          {plan.limits.max_storage_mb === -1 
                            ? 'Illimité' 
                            : `${plan.limits.max_storage_mb} Mo`}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Features */}
                  <ul className="mt-4 flex-1 space-y-2 text-sm text-zinc-600 dark:text-zinc-300">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-xs font-bold dark:bg-emerald-500/20 dark:text-emerald-300">
                          ✓
                        </span>
                        {getFeatureLabel(feature, 'fr')}
                      </li>
                    ))}
                  </ul>
                  
                  {/* Trial info */}
                  {plan.trial_days > 0 && !isCurrentPlan && !isFree && (
                    <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400 text-center">
                      {plan.trial_days} jours d'essai gratuit
                    </p>
                  )}
                  
                  {/* CTA */}
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={checkoutLoading || isCurrentPlan}
                    className={ctaClass[getCtaStyle(plan)]}
                  >
                    {checkoutLoading ? 'Chargement...' : isCurrentPlan ? 'Plan actuel' : isFree ? 'Commencer' : 'Choisir'}
                  </button>
                  
                  <div className="pointer-events-none absolute inset-0 rounded-3xl border border-zinc-100 opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:border-zinc-700/60" />
                </article>
              )
            })}
          </div>
        )}

        {/* Custom offer contact form */}
        <div className="mt-12 rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-100 via-sky-100 to-emerald-50 p-8 text-zinc-900 shadow-sm dark:border-emerald-500/30 dark:from-emerald-500/30 dark:via-sky-500/20 dark:to-emerald-500/10 dark:text-white">
          <h4 className="text-lg font-semibold">Besoin d'une offre personnalisée ?</h4>
          <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
            Support dédié, SLA garanti, SSO, hébergement isolé, volume personnalisé : décrivez vos besoins et nous vous proposerons une offre sur-mesure.
          </p>
          
          {contactSent ? (
            <div className="mt-6 p-4 rounded-xl bg-emerald-500/20 border border-emerald-500/30">
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                ✓ Votre demande a été envoyée ! Nous vous répondrons sous 24h.
              </p>
            </div>
          ) : (
            <form 
              className="mt-6 space-y-4"
              onSubmit={async (e) => {
                e.preventDefault()
                if (!contactMessage.trim()) return
                setContactSending(true)
                setContactError(null)
                try {
                  await api.post('/contact/custom-offer', {
                    email: user?.email,
                    name: contactName || undefined,
                    message: contactMessage
                  })
                  setContactSent(true)
                } catch (err: any) {
                  setContactError(err?.response?.data?.detail || 'Une erreur est survenue')
                } finally {
                  setContactSending(false)
                }
              }}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">Email</label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-3 py-2 text-sm rounded-lg bg-white/50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">Nom</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Votre nom"
                    className="w-full px-3 py-2 text-sm rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">Décrivez vos besoins</label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Ex: Nous avons besoin de 50 agents avec un volume de 100 000 messages/mois, hébergement dédié..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  required
                />
              </div>
              {contactError && (
                <p className="text-sm text-red-500">{contactError}</p>
              )}
              <button
                type="submit"
                disabled={contactSending || !contactMessage.trim()}
                className="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50"
              >
                {contactSending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Envoyer ma demande
              </button>
            </form>
          )}
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-6">Questions fréquentes</h3>
          <div className="divide-y divide-zinc-200 dark:divide-zinc-700/50">
            {FAQ_ITEMS.map((faq, idx) => (
              <div key={idx} className="py-4">
                <button
                  onClick={() => setFaqOpenIndex(faqOpenIndex === idx ? null : idx)}
                  className="flex w-full items-start justify-between text-left"
                >
                  <span className="text-sm font-medium text-zinc-900 dark:text-white">{faq.q}</span>
                  <ChevronDown
                    className={cn(
                      "h-5 w-5 text-zinc-400 transform transition-transform duration-200 ml-4 flex-shrink-0",
                      faqOpenIndex === idx ? "rotate-180" : "rotate-0"
                    )}
                  />
                </button>
                <AnimatePresence>
                  {faqOpenIndex === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 pr-8">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
